import { useEffect, useRef, useState } from "react";
import { Page } from "react-pdf";

import { usePdfPageWindow } from "../../Utils/usePdfPageWindow";
import "./PdfThumbnail.css";

const THUMBNAIL_SCALE = 0.15;
// US Letter at scale 1, used until the real page size is known.
const FALLBACK_PAGE_SIZE = { width: 612, height: 792 };

const thumbnailSize = (pageSizes, baselineSize, pageNumber) => {
  const size = pageSizes?.[pageNumber] || baselineSize || FALLBACK_PAGE_SIZE;
  return {
    width: size.width * THUMBNAIL_SCALE,
    height: size.height * THUMBNAIL_SCALE,
  };
};

// Same shell styling as DocumentModal's rail.
const railStyle = {
  paddingLeft: "10px",
  paddingRight: "10px",
  width: "120px",
  minWidth: "120px",
  backgroundColor: "var(--primary-10)",
  scrollbarWidth: "none",
};

/**
 * Reordering state for the rail.
 *
 * Lives in a hook because the rail shell and the thumbnails it contains are
 * now rendered from different places — the thumbnails are portaled out of the
 * viewer's <Document> so they can share its single load — and both halves need
 * the same drag handlers.
 */
export const useThumbnailDragAndDrop = ({ pageOrders, setPageOrders, docId, mergePDFs }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedPdf, setDraggedPdf] = useState(null); // Track which PDF the dragged page is from

  // Handle the drag start event, set the dragged index and which PDF it's coming from
  const handleDragStart = (event, index, pdfType) => {
    setDraggedIndex(index);
    setDraggedPdf(pdfType); // Set the PDF type ("documentURL" or "imagePdf")
  };

  // Allow the drop event
  const handleDragOver = (event) => {
    event.preventDefault(); // Allow dropping
  };

  // Handle the drop event
  const handleDrop = (event, targetIndex, targetPdfType) => {
    event.preventDefault();

    // Proceed only if the dragged page is from a different PDF
    if (draggedIndex !== null) {
      setPageOrders((prevOrders) => {
        const currentOrder = prevOrders[docId] || [];

        let newOrder;

        // If dragged from documentURL to imagePdf (or vice versa), update the order
        if (draggedPdf === "documentURL" && targetPdfType === "documentURL") {
          newOrder = [...currentOrder]; // Start with the existing order of documentURL
          const draggedPage = newOrder[draggedIndex];
          newOrder.splice(draggedIndex, 1); // Remove the dragged page from documentURL
          newOrder.splice(targetIndex, 0, draggedPage); // Insert into imagePdf
        } else if (draggedPdf === "imagePdf" && targetPdfType === "documentURL") {
          mergePDFs(draggedIndex, targetIndex, pageOrders[docId] || []);
          newOrder = [...currentOrder]; // Start with the existing order of imagePdf
        }

        return {
          ...prevOrders,
          [docId]: newOrder,
        };
      });

      // Clear dragged index and PDF type after the drop
      setDraggedIndex(null);
      setDraggedPdf(null);
    }
  };

  return { handleDragStart, handleDragOver, handleDrop };
};

/**
 * The rail shell: the scrolling container for the document's thumbnails (an
 * empty element that PdfThumbnailPages is portaled into) plus the separate
 * rail for the locally generated image PDF, which is its own small file and
 * keeps its own <Document>.
 */
export const PdfThumbnail = ({
  scrollRef,
  portalRef,
  // onThumbnailClick,
  // imagePdf,
  // fullPreview,
  dnd,
}) => {
  // const [numImagePages, setNumImagePages] = useState(null);

  // Set up for loading the number of pages from the imagePdf
  // const onLoadSuccess = ({ numPages }) => {
  //   setNumImagePages(numPages);
  // };

  return (
    <div className="d-flex">
      {/* First PDF Thumbnail (documentURL) */}
      <div
        className="pdf-thumbnail"
        style={railStyle}
        ref={scrollRef}
        onDragOver={dnd.handleDragOver}
        onDrop={(e) => dnd.handleDrop(e, -1, "documentURL")}
      >
        <div className="pdf-thumbnail-pages" ref={portalRef} />
      </div>

      {/* Second PDF Thumbnail (imagePdf) */}
      {/* {imagePdf && fullPreview && (
        <div className="pdf-thumbnail" style={railStyle}>
          <Document
            file={imagePdf}
            options={PDF_LOAD_OPTIONS}
            onLoadSuccess={onLoadSuccess}
            loading={""}
          >
            {Array.from({ length: numImagePages }, (_, index) => (
              <div
                key={index}
                id={`thumbnail_image_pdf_page_${index}`}
                className="thumbnail-page"
                style={{
                  marginTop: "10px",
                  cursor: "grab",
                }}
                draggable
                onDragStart={(e) => dnd.handleDragStart(e, index, "imagePdf")}
                onDragOver={dnd.handleDragOver}
                onDrop={(e) => dnd.handleDrop(e, index, "imagePdf")}
                onClick={() => onThumbnailClick(`image_pdf_page_${index}`)}
              >
                <Page
                  scale={THUMBNAIL_SCALE}
                  pageNumber={index + 1} // Note: Page numbers start from 1 in imagePdf
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
            ))}
          </Document>
        </div>
      )} */}
    </div>
  );
};

/**
 * The document's thumbnails. Rendered inside the shared <Document> and
 * portaled into the rail, so <Page> still finds react-pdf's DocumentContext.
 *
 * Wrappers stay mounted — that is what keeps every drop target present and the
 * rail scrolling correctly — while only thumbnails near the viewport mount a
 * canvas, since an offscreen canvas would pull its page's bytes over the wire.
 */
export const PdfThumbnailPages = ({
  pageOrder,
  selectedPages,
  currentPage,
  scrollRootEl,
  pageSizes,
  baselineSize,
  onThumbnailClick,
  setPageNumber,
  dnd,
}) => {
  const currentThumbnailRef = useRef(null);
  const pageOrderKey = pageOrder.join(",");

  // Keyed on the pdf page number rather than the slot, so reordering the rail
  // never invalidates what is already rendered. observationKey re-observes the
  // moved wrappers after a drag.
  const visiblePages = usePdfPageWindow(scrollRootEl, pageOrder.length, {
    selector: ".thumbnail-page",
    rootMargin: "100% 0px",
    initialKeys: pageOrder.length ? [pageOrder[0]] : [],
    observationKey: pageOrderKey,
  });

  // Keep the current page's thumbnail in view as the viewer scrolls.
  useEffect(() => {
    if (currentThumbnailRef.current) {
      currentThumbnailRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [currentPage]);

  return (
    <>
      {pageOrder.map((pageNumber, index) => {
        const { width, height } = thumbnailSize(pageSizes, baselineSize, pageNumber);
        // `currentPage` is the viewer's slot index (data-page-number), not the
        // pdf page number — compare against the slot.
        const isCurrent = currentPage === index + 1;
        const isSelected = selectedPages.includes(pageNumber);

        return (
          <div
            key={pageNumber}
            id={`thumbnail_pdf_page_${pageNumber}`}
            data-window-key={pageNumber}
            className={`thumbnail-page ${isCurrent ? "page-selected" : ""} ${
              isSelected ? "page-selected-red" : ""
            }`}
            style={{
              marginTop: "10px",
              cursor: "grab",
              width,
              height,
            }}
            ref={isCurrent ? currentThumbnailRef : null}
            draggable
            onDragStart={(e) => dnd.handleDragStart(e, index, "documentURL")}
            onDragOver={dnd.handleDragOver}
            onDrop={(e) => {
              e.stopPropagation();
              dnd.handleDrop(e, index, "documentURL");
            }}
            onClick={() => onThumbnailClick(`pdf_page_${pageNumber}`, setPageNumber)}
          >
            {visiblePages.has(pageNumber) && (
              <Page
                scale={THUMBNAIL_SCALE}
                pageNumber={pageNumber}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

export default PdfThumbnail;
